import React from 'react';
import { CellParamsDef } from '@grnhse/seedling/lib/birch/components/table';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { SecondaryBody } from '@grnhse/seedling/lib/birch/typography';
import { Chip } from '@grnhse/seedling/lib/birch/components/chips';
import BaseCandidateCell from 'talent_matching/components/shared/base_candidate_cell';
import BaseResumeDetailsCell from 'talent_matching/components/shared/base_resume_details_cell';

import BaseMatchStrengthCell from 'talent_matching/components/shared/base_match_strength_cell';
import CompanyTitleField from 'talent_matching/components/shared/company_title_field';
import MatchPercentageCell from 'talent_matching/my_greenhouse/components/table/match_percentage_cell';
import MatchReportCell from 'talent_matching/my_greenhouse/components/table/match_report_cell';
import { useMyGreenhouseCandidate } from 'talent_matching/my_greenhouse/hooks/use_my_greenhouse_candidates';

import { useUserContext } from 'talent_matching/contexts/user_context';
import { withLocalePrefix } from 'shared/utils/translation';
import { HelpTooltip } from '@grnhse/seedling/lib/birch/components/tooltips';

const t = withLocalePrefix('talent_matching');

const CandidateCellWithData = ({ candidateId }: { candidateId: number }) => {
  const { candidate } = useMyGreenhouseCandidate(candidateId);
  const { canUseLocations } = useUserContext();

  if (!candidate) {
    return null;
  }

  const resumeEmployment = candidate.resume?.details?.employments?.[0];
  const resumeAddress = candidate.resume?.details?.address;

  return (
    <BaseCandidateCell candidate={candidate}>
      {resumeEmployment && (
        <CompanyTitleField
          title=""
          company=""
          latestEmployment={null}
          resumeEmployment={resumeEmployment}
        />
      )}
      {canUseLocations && resumeAddress && (
        <SecondaryBody metadata className="pii-blur">
          {resumeAddress}
        </SecondaryBody>
      )}
    </BaseCandidateCell>
  );
};

const ResumeCellWithData = ({ candidateId }: { candidateId: number }) => {
  const { candidate } = useMyGreenhouseCandidate(candidateId);

  if (!candidate) {
    return null;
  }

  const resume = candidate.resume;
  const employments = resume?.details?.employments || [];

  return (
    <BaseResumeDetailsCell
      employments={employments}
      parsingStatus={resume?.parsing_status}
      resumeExists={!!resume}
    />
  );
};

const MatchPercentageCellWithData = ({
  candidateId,
}: {
  candidateId: number;
}) => {
  const { candidate } = useMyGreenhouseCandidate(candidateId);

  if (!candidate) {
    return null;
  }

  return <MatchPercentageCell matchScore={candidate.matchScore ?? null} />;
};

const MatchReportCellWithData = ({ candidateId }: { candidateId: number }) => {
  const { candidate } = useMyGreenhouseCandidate(candidateId);

  if (!candidate) {
    return null;
  }

  return <MatchReportCell matchReport={candidate.matchReport} />;
};

const MatchScoreCellWithData = ({ candidateId }: { candidateId: number }) => {
  const { candidate } = useMyGreenhouseCandidate(candidateId);

  if (!candidate) {
    return null;
  }

  return (
    <BaseMatchStrengthCell
      matchStrength={candidate.matchStrength ?? null}
      matchScore={candidate.matchScore ?? null}
      matchedSkills={candidate.matchedSkills || []}
      numCalibrationSkills={candidate.numCalibrationSkills ?? 0}
      overrideMatchStrength={candidate.overrideMatchStrength}
      resumeParsingStatus={candidate.resume?.parsing_status}
    />
  );
};

const WorkPreferencesCellWithData = ({
  candidateId,
}: {
  candidateId: number;
}) => {
  const { candidate } = useMyGreenhouseCandidate(candidateId);

  if (!candidate?.workPreferences) {
    return (
      <Stack
        direction="row"
        sx={{ height: '100%', padding: 1, paddingLeft: 0 }}
      >
        <SecondaryBody>{'--'}</SecondaryBody>
      </Stack>
    );
  }

  const {
    employmentTypes = [],
    worksites = [],
    willingToRelocate,
  } = candidate.workPreferences;
  const chips = [
    ...employmentTypes.map(
      (type) =>
        t(`panel.work_preferences.employment_type_values.${type}`) || type
    ),
    ...worksites.map(
      (ws) => t(`panel.work_preferences.worksite_values.${ws}`) || ws
    ),
    ...(willingToRelocate
      ? [t('panel.work_preferences.willing_to_relocate')]
      : []),
  ];

  if (chips.length === 0) {
    return <SecondaryBody>{'--'}</SecondaryBody>;
  }

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', height: '100%' }}>
      <Stack
        direction="row"
        spacing={0.5}
        sx={{ flexWrap: 'wrap', padding: 1, paddingLeft: 0 }}
      >
        {chips.map((label) => (
          <Chip key={label} text={label} />
        ))}
      </Stack>
    </div>
  );
};

export const useMyGreenhouseColumns = () => {
  return [
    {
      field: 'fullName',
      headerName: t('table.columns.job_seeker'),
      minWidth: 250,
      cell: (params: CellParamsDef) => (
        <CandidateCellWithData candidateId={params.row.id} />
      ),
    },
    {
      field: 'resumeDetails',
      headerName: t('resume'),
      minWidth: 250,
      cell: (params: CellParamsDef) => (
        <ResumeCellWithData candidateId={params.row.id} />
      ),
    },
    {
      field: 'matchScore',
      headerName: t('table.columns.match_strength.title'),
      minWidth: 150,
      renderHeader: () => (
        <Stack direction="row" spacing={1}>
          <SecondaryBody medium>
            {t('table.columns.match_strength.title')}
          </SecondaryBody>
          <HelpTooltip
            ariaLabel={t('table.columns.match_strength.description')}
            label={t('table.columns.match_strength.description')}
          />
        </Stack>
      ),
      cell: (params: CellParamsDef) => (
        <MatchScoreCellWithData candidateId={params.row.id} />
      ),
    },
    {
      field: 'matchPercentage',
      headerName: t('table.columns.match_percentage'),
      minWidth: 120,
      cell: (params: CellParamsDef) => (
        <MatchPercentageCellWithData candidateId={params.row.id} />
      ),
    },
    {
      field: 'matchReport',
      headerName: t('table.columns.match_report.title'),
      minWidth: 200,
      cell: (params: CellParamsDef) => (
        <MatchReportCellWithData candidateId={params.row.id} />
      ),
    },
    {
      field: 'workPreferences',
      headerName: t('filter_row.work_preferences.title'),
      minWidth: 200,
      cell: (params: CellParamsDef) => (
        <WorkPreferencesCellWithData candidateId={params.row.id} />
      ),
    },
  ];
};
